/**
 * 벽돌 강도(strength) → 색상/음정 매핑 (순수 — Phaser/WebAudio 비의존).
 *
 * `strength01`(0=약, 1=강)은 블록의 `hpMultiplier`(=칸 수)를 0~1로 정규화한 값이며,
 * 색(무지개 hue)과 음(도레미파솔라시)이 모두 이 한 값에서 파생된다 → 같은 강도 = 같은 색 + 같은 음.
 * - 색: 약함=빨강(hue 0°) → 강함=보라(hue 280°). 명도/채도는 남은 HP 비율로 보간(데미지 시 어두워짐).
 * - 음: 약함=높은 음(시 493.88Hz) → 강함=낮은 음(도 261.63Hz). 사용자 지정 방향(색과 반대).
 */

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);
const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

/** hpMultiplier(1..cap) → strength01(0..1). cap<=1이면 0 (단일 강도 그리드 방어). */
export function brickStrength01(hpMultiplier: number, cap: number): number {
  if (cap <= 1) return 0;
  return clamp01((hpMultiplier - 1) / (cap - 1));
}

/** 도레미파솔라시 4옥타브 주파수(Hz). */
export const NOTE_HZ = {
  brick_note_do: 261.63,
  brick_note_re: 293.66,
  brick_note_mi: 329.63,
  brick_note_fa: 349.23,
  brick_note_sol: 392.0,
  brick_note_la: 440.0,
  brick_note_si: 493.88,
} as const;

export type BrickNoteId = keyof typeof NOTE_HZ;

/**
 * 강도 약→강 순서의 음 배열. 약함=시(높은 음) → 강함=도(낮은 음).
 * SoundController가 이 id들로 음계 def를 카탈로그에 등록한다.
 */
export const NOTE_IDS_WEAK_TO_STRONG: readonly BrickNoteId[] = [
  "brick_note_si",
  "brick_note_la",
  "brick_note_sol",
  "brick_note_fa",
  "brick_note_mi",
  "brick_note_re",
  "brick_note_do",
];

/** strength01 → 음 id. round(s*6)으로 7음 균등 분할. */
export function strengthToNoteId(strength01: number): BrickNoteId {
  const last = NOTE_IDS_WEAK_TO_STRONG.length - 1;
  const idx = Math.min(last, Math.max(0, Math.round(clamp01(strength01) * last)));
  return NOTE_IDS_WEAK_TO_STRONG[idx]!;
}

/** strength01 → 음 주파수(Hz). */
export function strengthToNoteHz(strength01: number): number {
  return NOTE_HZ[strengthToNoteId(strength01)];
}

/** 무지개 hue 상한(도). 빨강(0)→보라(280). */
const HUE_MAX_DEG = 280;

/**
 * 강도 + 남은 HP 비율 → HSV (h/s/v 모두 0~1).
 * h: 약함=빨강(0) → 강함=보라. s: 데미지로 짙어짐. v: 데미지로 어두워짐(가득=밝음).
 * Phaser.Display.Color.HSVToRGB(h, s, v)에 그대로 전달 가능.
 */
export function strengthToHsv(
  strength01: number,
  hpRatio01: number,
): { h: number; s: number; v: number } {
  const h = (clamp01(strength01) * HUE_MAX_DEG) / 360;
  const damage01 = 1 - clamp01(hpRatio01);
  const s = lerp(0.35, 0.95, damage01);
  const v = lerp(1, 0.78, damage01);
  return { h, s, v };
}
